import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader } from 'semantic-ui-react';
import {
  useGetProductQuery,
  useUpdateProductMutation,
  useGetProductKnowledgeEvaluationsQuery,
  useGetQuizQuestionsQuery,
} from './productKnowledgeApiSlice';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { shortDate } from '../chat_sessions/MobileChatSessionList';
import {
  Dash, DetailPageShell, DetailTabs, FormField, FormGrid, FormSection, ListNote, ListTable, SaveBar, Tag, ToggleField,
  clamp2, inputStyle, listCard, monoTextareaStyle, scoreColor, scoreText, stack,
} from '../../components/listPage';

/** 제품 상세: 기본 정보 (edit), 제품 지식 평가, 퀴즈 문제 — on the shared detail layout. */

const TABS = ['basic', 'evaluations', 'questions'];
const EMPTY = { name: '', category: '', support_url: '', parsed_specs: '', release_year: '', apple_pk: '', tech_specs_url: '', tech_specs_pk: '', is_active: true };

const EvaluationsTab = ({ productId, isMobile }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetProductKnowledgeEvaluationsQuery({ product: productId });
  const summary = e => e.result?.summary || '';
  return (
    <ListTable
      isMobile={isMobile}
      loading={isLoading}
      rows={data?.results || []}
      empty="이 제품에 대한 평가 기록이 없습니다."
      onRowClick={e => navigate(`/chatsessions/${e.chat_session}`)}
      columns={[
        { key: 'date', label: '날짜', muted: true, render: e => shortDate(e.created_at) },
        { key: 'session', label: '롤플레이', render: e => <span style={{ fontWeight: 600 }}>{e.chat_session_str || '롤플레이'}</span> },
        { key: 'score', label: '점수', render: e => <span style={{ fontWeight: 600, color: scoreColor(e.score) }}>{scoreText(e.score)}</span> },
        { key: 'claims', label: '항목 수', align: 'center', render: e => e.extracted_claims?.length || 0 },
        { key: 'summary', label: '평가 요약', render: e => (summary(e) ? <div style={{ ...clamp2, color: '#424245' }}>{summary(e)}</div> : <Dash />) },
      ]}
      card={{
        title: e => e.chat_session_str || '롤플레이',
        tags: e => [<Tag key="s" tone={e.score >= 80 ? 'green' : e.score >= 50 ? 'amber' : 'red'}>{scoreText(e.score)}</Tag>],
        meta: e => [shortDate(e.created_at), `항목 ${e.extracted_claims?.length || 0}개`],
        body: summary,
      }}
    />
  );
};

const QuestionsTab = ({ productId, isMobile }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetQuizQuestionsQuery({ product: productId });
  const type = q => <Tag tone="gray">{q.question_type === 'OE' ? '서술형' : '객관식'}</Tag>;
  const active = q => (q.is_active ? <Tag tone="green">사용 중</Tag> : <Tag tone="gray">비활성</Tag>);
  return (
    <ListTable
      isMobile={isMobile}
      loading={isLoading}
      rows={data?.results || []}
      empty="이 제품에 대한 퀴즈가 없습니다."
      onRowClick={q => navigate(`/admin/quiz-questions/${q.id}`)}
      columns={[
        { key: 'question', label: '질문', render: q => <div style={{ ...clamp2, color: '#424245' }}>{q.question_text}</div> },
        { key: 'type', label: '유형', render: type },
        { key: 'active', label: '상태', render: active },
        { key: 'target', label: '대상', nowrap: true, render: q => q.user_display_name || <span style={{ color: '#86868b' }}>공통</span> },
      ]}
      card={{
        title: q => (q.question_type === 'OE' ? '서술형' : '객관식'),
        tags: q => [<React.Fragment key="a">{active(q)}</React.Fragment>],
        meta: q => [q.user_display_name ? `맞춤 · ${q.user_display_name}` : '공통'],
        body: q => q.question_text,
      }}
    />
  );
};

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useBreakpoint();
  const tab = Math.max(0, TABS.indexOf(new URLSearchParams(location.search).get('tab') || 'basic'));

  const { data: product, isLoading, error } = useGetProductQuery(id);
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();
  const [form, setForm] = useState(EMPTY);
  const [isDirty, setIsDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (product) setForm(Object.fromEntries(Object.keys(EMPTY).map(k => [k, product[k] ?? EMPTY[k]])));
  }, [product]);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setIsDirty(true);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    try {
      await updateProduct({ id, ...form }).unwrap();
      setIsDirty(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      const fieldErrors = err?.data && typeof err.data === 'object'
        ? Object.entries(err.data).map(([field, messages]) => `${field}: ${[].concat(messages).join(' ')}`)
        : [];
      setSaveError(fieldErrors.length ? fieldErrors : ['제품 정보를 저장하지 못했습니다.']);
    }
  };

  const input = (k, props = {}) => <input style={inputStyle} value={form[k]} onChange={e => set(k, e.target.value)} {...props} />;

  if (isLoading) return <Loader active />;

  return (
    <DetailPageShell
      wide
      isMobile={isMobile}
      title={product?.name || '제품'}
      subtitle={[product?.category, product?.release_year].filter(Boolean).join(' · ')}
      backTo="/admin/products"
    >
      {error ? (
        <div style={{ ...listCard, textAlign: 'center', color: '#86868b' }}>제품 정보를 불러오지 못했습니다.</div>
      ) : (
        <>
          <DetailTabs
            tabs={[{ label: '기본 정보' }, { label: '제품 지식 평가' }, { label: '퀴즈 문제' }]}
            active={tab}
            onChange={i => navigate(`?tab=${TABS[i]}`, { replace: true })}
          />
          {tab === 0 && (
            <div style={stack}>
              {saveSuccess && <ListNote tone="green">제품 정보를 저장했습니다.</ListNote>}
              {saveError && <ListNote tone="red">{saveError.join(' · ')}</ListNote>}
              <FormSection title="기본 정보">
                <FormGrid isMobile={isMobile}>
                  <FormField label="제품명">{input('name')}</FormField>
                  <FormField label="카테고리">{input('category')}</FormField>
                  <FormField label="출시 연도">{input('release_year', { type: 'number' })}</FormField>
                  <FormField label="활성 상태"><ToggleField checked={!!form.is_active} onChange={v => set('is_active', v)} /></FormField>
                </FormGrid>
              </FormSection>
              <FormSection title="외부 링크 & 키">
                <FormGrid isMobile={isMobile}>
                  <FormField label="지원 URL">{input('support_url')}</FormField>
                  <FormField label="Apple PK">{input('apple_pk')}</FormField>
                  <FormField label="Tech Specs URL">{input('tech_specs_url')}</FormField>
                  <FormField label="Tech Specs PK">{input('tech_specs_pk')}</FormField>
                </FormGrid>
              </FormSection>
              <FormSection title="상세 사양">
                <FormField label="파싱된 사양 데이터">
                  <textarea style={{ ...monoTextareaStyle, minHeight: '260px' }} value={form.parsed_specs} onChange={e => set('parsed_specs', e.target.value)} />
                </FormField>
              </FormSection>
              <SaveBar onSave={handleSave} disabled={!isDirty} saving={isUpdating} />
            </div>
          )}
          {tab === 1 && <EvaluationsTab productId={id} isMobile={isMobile} />}
          {tab === 2 && <QuestionsTab productId={id} isMobile={isMobile} />}
        </>
      )}
    </DetailPageShell>
  );
};

export default ProductDetails;
